import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Home, FileText, AlertTriangle, Shield, Users, GraduationCap, LogOut, ClipboardCheck, FileInput } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';

const AdminNav = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const navItems = [
    { path: '/admin', icon: Home, label: t('nav.home'), exact: true },
    { path: '/admin/jsas', icon: FileText, label: t('nav.jsa') },
    { path: '/admin/incidents', icon: AlertTriangle, label: t('nav.incidents') },
    { path: '/admin/near-miss', icon: AlertTriangle, label: t('nav.nearMiss') },
    { path: '/admin/audits', icon: Shield, label: t('nav.audits') },
    { path: '/admin/users', icon: Users, label: t('nav.users') },
    { path: '/admin/training', icon: GraduationCap, label: t('nav.training') },
    { path: '/admin/new-hires', icon: ClipboardCheck, label: t('nav.newHire') },
    { path: '/admin/custom-forms', icon: FileInput, label: 'PDF Forms' },
    { path: '/admin/field-logs', icon: FileText, label: t('fieldLogs.title') },
  ];

  const isActive = (path, exact) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="bg-white border-b shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo/Brand */}
          <div className="flex items-center">
            <Shield className="w-8 h-8 text-orange-600 mr-3" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">SafetyMind AI</h1>
              <p className="text-xs text-gray-500 italic">Safety That Thinks</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Button
                key={item.path}
                variant={isActive(item.path, item.exact) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => navigate(item.path)}
                className={isActive(item.path, item.exact) ? 'bg-orange-600 hover:bg-orange-700' : ''}
              >
                <item.icon className="w-4 h-4 mr-2" />
                {item.label}
              </Button>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.role}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden pb-3 flex flex-wrap gap-2">
          {navItems.map((item) => (
            <Button
              key={item.path}
              variant={isActive(item.path, item.exact) ? 'default' : 'outline'}
              size="sm"
              onClick={() => navigate(item.path)}
              className={isActive(item.path, item.exact) ? 'bg-orange-600' : ''}
            >
              <item.icon className="w-4 h-4 mr-1" />
              {item.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminNav;
